import React, { memo, useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import AuthShell, { canAutoFocus } from "../Auth/AuthShell";
import {
  AuthField,
  FormAlert,
  PasswordField,
  SubmitLabel,
} from "../Auth/AuthField";
import s from "../Auth/Auth.module.css";
import { useAuth } from "../../contexts/JWTContext";

const REMEMBER_LOGIN_KEY = "landingRememberedLogin";

const emptyRememberedLogin = {
  username: "",
  password: "",
  rememberPassword: false,
};

const saveRememberedLogin = ({ username, password, rememberPassword }) => {
  try {
    if (!rememberPassword) {
      localStorage.removeItem(REMEMBER_LOGIN_KEY);
      return;
    }

    localStorage.setItem(
      REMEMBER_LOGIN_KEY,
      JSON.stringify({
        username,
        password,
        rememberPassword: true,
      }),
    );
  } catch {}
};

const clearRememberedLogin = () => {
  try {
    localStorage.removeItem(REMEMBER_LOGIN_KEY);
  } catch {}
};

const getRememberedLogin = () => {
  try {
    const rawValue = localStorage.getItem(REMEMBER_LOGIN_KEY);
    if (!rawValue) {
      return emptyRememberedLogin;
    }

    const parsedValue = JSON.parse(rawValue);
    return {
      username: parsedValue?.username || "",
      password: parsedValue?.password || "",
      rememberPassword: Boolean(parsedValue?.rememberPassword),
    };
  } catch {
    return emptyRememberedLogin;
  }
};

const LoginModal = memo(({ isOpen, onClose, onOpenRegister, hideClose }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(() => {
    const rememberedLogin = getRememberedLogin();
    return {
      username: rememberedLogin.username,
      password: rememberedLogin.password,
    };
  });
  const [rememberPassword, setRememberPassword] = useState(
    () => getRememberedLogin().rememberPassword,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  // track if modal was opened via global event
  const [openFromEvent, setOpenFromEvent] = useState(false);

  const closeModal = useCallback(() => {
    const nextRememberedLogin = getRememberedLogin();
    setOpenFromEvent(false);
    setError("");
    setFieldErrors({});
    setRememberPassword(nextRememberedLogin.rememberPassword);
    setFormData({
      username: nextRememberedLogin.rememberPassword
        ? nextRememberedLogin.username
        : "",
      password: nextRememberedLogin.rememberPassword
        ? nextRememberedLogin.password
        : "",
    });
    if (onClose) onClose();
  }, [onClose]);

  useEffect(() => {
    const onOpenLogin = () => {
      setError("");
      setFieldErrors({});
      const nextRememberedLogin = getRememberedLogin();
      setRememberPassword(nextRememberedLogin.rememberPassword);
      setFormData({
        username: nextRememberedLogin.username,
        password: nextRememberedLogin.password,
      });
      setOpenFromEvent(true);
    };
    window.addEventListener("open-login-modal", onOpenLogin);
    return () => window.removeEventListener("open-login-modal", onOpenLogin);
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    setError("");
  }, []);

  const handleRememberPasswordChange = useCallback(
    (event) => {
      const checked = event.target.checked;
      setRememberPassword(checked);

      if (!checked) {
        clearRememberedLogin();
        return;
      }

      saveRememberedLogin({
        username: formData.username.trim(),
        password: formData.password,
        rememberPassword: true,
      });
    },
    [formData.password, formData.username],
  );

  useEffect(() => {
    if (!rememberPassword) {
      return;
    }

    saveRememberedLogin({
      username: formData.username.trim(),
      password: formData.password,
      rememberPassword: true,
    });
  }, [formData.password, formData.username, rememberPassword]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");

      const nextErrors = {};
      if (!formData.username.trim()) {
        nextErrors.username = "กรุณากรอกเบอร์โทรศัพท์";
      }
      if (!formData.password) nextErrors.password = "กรุณากรอกรหัสผ่าน";
      setFieldErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        await login(formData.username.trim(), formData.password);

        if (rememberPassword) {
          saveRememberedLogin({
            username: formData.username.trim(),
            password: formData.password,
            rememberPassword: true,
          });
        } else {
          clearRememberedLogin();
        }

        setLoading(false);
        setFormData({
          username: rememberPassword ? formData.username.trim() : "",
          password: rememberPassword ? formData.password : "",
        });
        closeModal();
        navigate("/home", { replace: true });
      } catch (err) {
        setLoading(false);
        const body = err?.response?.data;
        setError(
          (typeof body === "string" && body) ||
            body?.message ||
            err?.message ||
            "เข้าสู่ระบบไม่สำเร็จ",
        );
      }
    },
    [formData, login, closeModal, navigate, rememberPassword],
  );

  const handleOpenRegister = useCallback(() => {
    closeModal();
    if (onOpenRegister) {
      onOpenRegister();
    } else {
      window.dispatchEvent(new CustomEvent("open-register-modal"));
    }
  }, [onOpenRegister, closeModal]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const nextRememberedLogin = getRememberedLogin();
    setRememberPassword(nextRememberedLogin.rememberPassword);
    setFormData({
      username: nextRememberedLogin.username,
      password: nextRememberedLogin.password,
    });
  }, [isOpen]);

  if (!isOpen && !openFromEvent) return null;

  return (
    <AuthShell
      mode="login"
      onSwitchMode={handleOpenRegister}
      onClose={closeModal}
      onDismiss={closeModal}
      hideClose={hideClose}
      aside={
        <>
          <span className="lux-eyebrow">Welcome back</span>
          <p className={s.asideTitle}>
            ยินดีต้อนรับ
            <br />
            <b className="lux-gold-text">กลับมาอีกครั้ง</b>
          </p>
          <p className={s.asideText}>เข้าสู่ระบบเพื่อเล่นต่อได้ทันที</p>
        </>
      }
    >
      <form className={`${s.form} ${s.formCenter}`} onSubmit={handleSubmit} noValidate>
        <div className={s.formHead}>
          <strong>เข้าสู่บัญชีของคุณ</strong>
          <span>กรอกเบอร์โทรศัพท์และรหัสผ่านเพื่อเข้าสู่ระบบ</span>
        </div>

        <AuthField
          label="เบอร์โทรศัพท์"
          icon="solar:phone-calling-rounded-linear"
          type="text"
          inputMode="tel"
          name="username"
          autoComplete="username"
          placeholder="กรอกเบอร์โทรศัพท์"
          value={formData.username}
          onChange={handleInputChange}
          error={fieldErrors.username}
          autoFocus={canAutoFocus()}
        />

        <PasswordField
          label="รหัสผ่าน"
          icon="solar:lock-keyhole-minimalistic-linear"
          name="password"
          autoComplete="current-password"
          placeholder="กรอกรหัสผ่าน"
          value={formData.password}
          onChange={handleInputChange}
          error={fieldErrors.password}
        />

        <label className={s.remember}>
          <input
            type="checkbox"
            checked={rememberPassword}
            onChange={handleRememberPasswordChange}
          />
          <span className={s.check} aria-hidden="true">
            <Icon icon="ic:round-check" width={16} />
          </span>
          <span className={s.rememberText}>จดจำรหัสผ่าน</span>
        </label>

        <FormAlert>{error}</FormAlert>

        <button
          type="submit"
          className="lux-btn lux-btn--gold lux-btn--lg lux-btn--block"
          disabled={loading}
        >
          <SubmitLabel loading={loading} loadingText="กำลังเข้าสู่ระบบ...">
            <span>เข้าสู่ระบบ</span>
          </SubmitLabel>
        </button>

        <div className={s.formFoot}>
          <span>ยังไม่มีบัญชี?</span>
          <button type="button" className={s.textBtn} onClick={handleOpenRegister}>
            สมัครสมาชิก
          </button>
        </div>
      </form>
    </AuthShell>
  );
});

LoginModal.displayName = "LoginModal";

export default LoginModal;
